export interface ModuleDetail {
  id: string;
  title: string;
  shortDescription: string;
  detailedDescription: string;
  features: string[];
  benefits: string[];
  useCases: string[];
}

export const moduleDetails: ModuleDetail[] = [
  {
    id: 'avo-procure',
    title: 'AVO Procure',
    shortDescription: 'Streamlined procurement planning, requisition management, and purchase order processing with automated workflows.',
    detailedDescription: 'AVO Procure revolutionizes your procurement process with intelligent automation and comprehensive workflow management. From initial requisition to final purchase order, every step is optimized for efficiency and compliance.',
    features: [
      'Automated requisition approval workflows',
      'Real-time budget tracking and alerts',
      'Supplier performance monitoring',
      'Compliance and audit trail management',
      'Multi-currency support',
      'Integration with ERP systems'
    ],
    benefits: [
      'Reduce procurement cycle time by 60%',
      'Improve spend visibility and control',
      'Ensure regulatory compliance',
      'Streamline approval processes'
    ],
    useCases: [
      'Enterprise procurement departments',
      'Government agencies',
      'Healthcare organizations',
      'Manufacturing companies'
    ]
  },
  {
    id: 'avo-ai',
    title: 'AVO AI',
    shortDescription: 'Intelligent assistant for automation, data insights, and AI-powered recommendations to optimize business processes.',
    detailedDescription: 'AVO AI leverages advanced machine learning algorithms to provide intelligent insights and automate complex business processes. Our AI engine learns from your data to deliver personalized recommendations and predictive analytics.',
    features: [
      'Natural language processing for queries',
      'Predictive analytics and forecasting',
      'Automated document classification',
      'Intelligent workflow optimization',
      'Real-time anomaly detection',
      'Custom AI model training'
    ],
    benefits: [
      'Increase operational efficiency by 40%',
      'Reduce manual data processing time',
      'Improve decision-making accuracy',
      'Enable proactive problem resolution'
    ],
    useCases: [
      'Data analysis teams',
      'Operations managers',
      'Business intelligence departments',
      'Process optimization specialists'
    ]
  },
  {
    id: 'avo-ebidding',
    title: 'AVO eBidding',
    shortDescription: 'Advanced reverse and sealed bidding platform with real-time scoring and transparent evaluation processes.',
    detailedDescription: 'AVO eBidding provides a secure, transparent platform for competitive bidding processes. Whether reverse auctions or sealed bids, our platform ensures fair competition while maximizing value for your organization.',
    features: [
      'Real-time auction management',
      'Advanced scoring algorithms',
      'Bidder authentication and verification',
      'Automated bid evaluation',
      'Comprehensive audit trails',
      'Multi-language support'
    ],
    benefits: [
      'Reduce procurement costs by 15-25%',
      'Ensure transparent and fair processes',
      'Accelerate bid evaluation time',
      'Improve supplier competition'
    ],
    useCases: [
      'Procurement departments',
      'Government agencies',
      'Construction companies',
      'Service providers'
    ]
  },
  {
    id: 'avo-vendor-portal',
    title: 'AVO Vendor Portal',
    shortDescription: 'Comprehensive supplier onboarding, document management, and communication platform for seamless vendor collaboration.',
    detailedDescription: 'AVO Vendor Portal creates a centralized hub for all supplier interactions. From initial registration to ongoing collaboration, vendors have everything they need to succeed while maintaining your organization\'s standards.',
    features: [
      'Self-service vendor registration',
      'Document upload and management',
      'Real-time communication tools',
      'Performance tracking dashboards',
      'Automated compliance checks',
      'Multi-vendor collaboration spaces'
    ],
    benefits: [
      'Reduce vendor onboarding time by 70%',
      'Improve vendor satisfaction scores',
      'Centralize vendor communications',
      'Ensure consistent vendor standards'
    ],
    useCases: [
      'Procurement teams',
      'Vendor management departments',
      'Supply chain managers',
      'Compliance officers'
    ]
  },
  {
    id: 'avo-contract-lifecycle',
    title: 'AVO Contract Lifecycle',
    shortDescription: 'Digital contract templates, version control, and e-signature capabilities for end-to-end contract management.',
    detailedDescription: 'AVO Contract Lifecycle streamlines the entire contract process from creation to execution and renewal. Our platform ensures compliance, reduces risk, and accelerates contract finalization while maintaining full audit trails.',
    features: [
      'Smart contract templates',
      'Version control and tracking',
      'Electronic signature integration',
      'Automated approval workflows',
      'Contract performance monitoring',
      'Renewal and expiration alerts'
    ],
    benefits: [
      'Reduce contract cycle time by 50%',
      'Improve contract compliance rates',
      'Minimize legal risks',
      'Streamline approval processes'
    ],
    useCases: [
      'Legal departments',
      'Procurement teams',
      'HR departments',
      'Sales organizations'
    ]
  },
  {
    id: 'avo-analytics',
    title: 'AVO Analytics',
    shortDescription: 'Real-time dashboards, spend analysis, and performance KPIs with advanced reporting and visualization tools.',
    detailedDescription: 'AVO Analytics transforms your data into actionable insights with powerful visualization tools and real-time dashboards. Track performance, identify trends, and make data-driven decisions with confidence.',
    features: [
      'Real-time data visualization',
      'Custom dashboard creation',
      'Advanced reporting tools',
      'Predictive analytics',
      'Automated report generation',
      'Mobile-responsive dashboards'
    ],
    benefits: [
      'Improve decision-making speed',
      'Identify cost-saving opportunities',
      'Track performance metrics',
      'Enable proactive management'
    ],
    useCases: [
      'Executive leadership',
      'Data analysts',
      'Operations managers',
      'Business intelligence teams'
    ]
  },
  {
    id: 'avo-workflow',
    title: 'AVO Workflow',
    shortDescription: 'Visual builder for creating custom approval flows and business rules with drag-and-drop simplicity.',
    detailedDescription: 'AVO Workflow empowers business users to create and modify approval processes without technical expertise. Our visual workflow builder makes process automation accessible to everyone in your organization.',
    features: [
      'Drag-and-drop workflow builder',
      'Conditional logic and branching',
      'Role-based approval routing',
      'Real-time process monitoring',
      'Automated notifications',
      'Process performance analytics'
    ],
    benefits: [
      'Reduce process design time by 80%',
      'Eliminate manual routing errors',
      'Improve process transparency',
      'Enable rapid process changes'
    ],
    useCases: [
      'Business process managers',
      'Operations teams',
      'HR departments',
      'Finance teams'
    ]
  },
  {
    id: 'avo-invoicing',
    title: 'AVO Invoicing',
    shortDescription: 'Automated invoice validation, status tracking, and seamless integration with financial systems.',
    detailedDescription: 'AVO Invoicing automates the entire invoice processing cycle from receipt to payment. Our intelligent system validates invoices, routes approvals, and integrates seamlessly with your financial systems.',
    features: [
      'Automated invoice capture',
      'Intelligent data extraction',
      'Three-way matching validation',
      'Approval workflow automation',
      'Payment processing integration',
      'Dispute resolution tracking'
    ],
    benefits: [
      'Reduce invoice processing time by 75%',
      'Minimize payment errors',
      'Improve cash flow management',
      'Enhance vendor relationships'
    ],
    useCases: [
      'Accounts payable teams',
      'Finance departments',
      'Procurement teams',
      'Vendor management'
    ]
  },
  {
    id: 'avo-risk-monitor',
    title: 'AVO Risk Monitor',
    shortDescription: 'Comprehensive vendor performance scoring and compliance alerts with risk assessment and monitoring.',
    detailedDescription: 'AVO Risk Monitor provides continuous monitoring of vendor performance and compliance risks. Our advanced risk scoring algorithms help you identify potential issues before they impact your operations.',
    features: [
      'Real-time risk scoring',
      'Compliance monitoring',
      'Performance tracking',
      'Automated alert system',
      'Risk mitigation recommendations',
      'Regulatory reporting tools'
    ],
    benefits: [
      'Reduce supply chain disruptions',
      'Improve compliance rates',
      'Minimize operational risks',
      'Enable proactive risk management'
    ],
    useCases: [
      'Risk management teams',
      'Compliance officers',
      'Supply chain managers',
      'Executive leadership'
    ]
  }
]; 